// React imports
import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// Generic imporrts
import _ from 'lodash';

// Material UI imports
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import SignatureEditor from './Settings.signature';
import { useTheme, alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import IconButton from '@mui/material/IconButton';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEnvelopeOpenText, faCircleDollar, faTable, faChartMixed, faBuilding, 
    faAddressBook, faList, faPeopleGroup, faReplyClock, faToolbox, faUsers, faClipboard,
    faFileCheck, faHeading, faCogs, faChartArea, faEnvelopeDot,
    faUserTie, faBullseyeArrow, faCalculator, faMoneyCheckDollarPen,
    faChevronRight,
    faChevronLeft, faChartTreeMap, faShip} from '@fortawesome/pro-solid-svg-icons';
import { faFileDashedLine } from '@fortawesome/pro-regular-svg-icons';
import { faWpforms } from '@fortawesome/free-brands-svg-icons';

// Local imports
import { useColorMode } from '../../ColorModeContext';
import email from '../dashboard/utils/email';
import SidebarNotifications, { SidebarOutgoing } from './Sidebar.notifications';
import { Divider } from '@mui/material';

function Sidebar(props) {
    const navigate = useNavigate();
    const theme = useTheme();
    const colorMode = useColorMode();
    const { org, me, onLogout, opportunities, tradelines, notifications, onUpdateNotification, accounts, suppliers, 
        featureToggle = {}, accesstoken, emailLoginInfo, onEmailLogout, onEmailRefreshConnection, emailConversations,
        minimized, setMinimized
    } = props;
    const location = useLocation();
    const [accountMenuOpen, setAccountMenuOpen] = useState(false);
    const [accountMenuAnchorEl, setAccountMenuAnchorEl] = useState(null);
    const [signatureOpen, setSignatureOpen] = useState(false);
    const [emailMenuOpen, setEmailMenuOpen] = useState(false);
    const [emailMenuAnchorEl, setEmailMenuAnchorEl] = useState(null);

    const onAccountMenuOpen = (event) => {
        setAccountMenuAnchorEl(event.currentTarget);
        setAccountMenuOpen(true);
    };

    const onAccountMenuClose = () => {
        setAccountMenuOpen(false);
    };

    const onEmailMenuOpen = (event) => {
        setEmailMenuAnchorEl(event.currentTarget);
        setEmailMenuOpen(true);
    };

    const onEmailMenuClose = () => {
        setEmailMenuOpen(false);
    };

    const { manages = [] } = me;

    return (
        <div>
            <div style={{ position: 'fixed', left: '0px', top: '0px', height: '100%', width: minimized ? '60px' : '240px', zIndex: '-1', background: theme.palette.panel1, borderRight: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`}}>&nbsp;</div>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px' }}>
                {!minimized && (
                    <img src='/monada-wordmark.png' alt='Monada' style={{ height: '16px', width: 'auto' }} />
                )}
                <IconButton onClick={() => setMinimized(!minimized)}>
                    <FontAwesomeIcon icon={minimized ? faChevronRight : faChevronLeft} />
                </IconButton>
            </Box>
            {!minimized && (
                <div style={{ padding: '0px 12px' }}>
                    <hr style={{ opacity: '0.3', margin: '0px' }} />
                </div>
            )}
            {!minimized && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant='h6' sx={{ padding: '18px 12px', color: theme.palette.text.primary, cursor: 'pointer' }} onClick={() => navigate(`/organization/${org.alias}`)}>
                        {org.title}
                    </Typography>
                    {featureToggle['feature.darkmode'] && (
                        <Switch
                            checked={theme.palette.mode === 'dark'}
                            onChange={colorMode.toggleColorMode}
                            color='primary'
                        />
                    )}
                </Box>
            )}
            {!minimized && (
                <div style={{ padding: '0px 12px' }}>
                    <hr style={{ opacity: '0.3' }} />
                </div>
            )}
            {!minimized && (
                <List sx={{ padding: '4px 0px' }}>
                    <ListItem 
                        button 
                        sx={{ 
                            color: theme.palette.text.secondary,
                            padding: '4px 8px',
                            margin: '2px 4px',
                            borderRadius: '4px',
                            border: '1px solid transparent',
                            fontSize: '11px',
                            minHeight: '32px',
                            '&:hover': {
                                border: '1px solid',
                                borderColor: alpha(theme.palette.text.secondary, 0.2),
                                backgroundColor: alpha(theme.palette.text.secondary, 0.05),
                            },
                        }} 
                        onClick={onAccountMenuOpen}
                    >
                        <ListItemIcon sx={{ color: theme.palette.text.secondary, minWidth: '32px', '& svg': { fontSize: '14px' } }}>
                            <FontAwesomeIcon icon={faUserTie} />
                        </ListItemIcon>
                        <ListItemText primary={me.email} primaryTypographyProps={{ sx: { fontSize: '11px', color: theme.palette.text.secondary, fontWeight: 400 } }} />
                    </ListItem>
                </List>
            )}
            <Menu
                anchorEl={accountMenuAnchorEl}
                open={accountMenuOpen}
                onClose={onAccountMenuClose}
            >
                {org?.settings?.signature?.enabled && (
                    <MenuItem onClick={() => { onAccountMenuClose(); setSignatureOpen(true); }}>Email Signature</MenuItem>
                )}
                <MenuItem onClick={onLogout}>Logout</MenuItem>
            </Menu>
            {org?.settings?.signature?.enabled && (
                <Dialog open={signatureOpen} onClose={() => setSignatureOpen(false)} maxWidth='md' fullWidth>
                    <DialogContent>
                        <SignatureEditor me={me} accesstoken={accesstoken} organizationId={org.id} />
                    </DialogContent>
                </Dialog>
            )}
            {!minimized && (
                <div style={{ padding: '0px 12px' }}>
                    <hr style={{ opacity: '0.3' }} />
                </div>
            )}
            {!minimized && (
                <List sx={{ padding: '4px 0px' }}>
                    <ListItem 
                        button 
                        sx={{ 
                            color: theme.palette.text.secondary,
                            padding: '4px 8px',
                            margin: '2px 4px',
                            borderRadius: '4px',
                            border: '1px solid transparent',
                            fontSize: '11px',
                            minHeight: '32px',
                            '&:hover': {
                                border: '1px solid',
                                borderColor: alpha(theme.palette.text.secondary, 0.2),
                                backgroundColor: alpha(theme.palette.text.secondary, 0.05),
                            },
                        }} 
                        onClick={() => navigate(`/organization/${org.alias}/email`)}
                    >
                        <ListItemIcon sx={{ color: theme.palette.text.secondary, minWidth: '32px', '& svg': { fontSize: '14px' } }}>
                            <FontAwesomeIcon icon={faEnvelopeOpenText} />
                        </ListItemIcon>
                        {(() => {
                            if (!emailLoginInfo || !opportunities) return (
                                <ListItemText primary={'Connecting...'} primaryTypographyProps={{ sx: { fontSize: '11px', color: theme.palette.text.secondary, fontWeight: 400 } }} />
                            )
                            if (emailLoginInfo.status === 'not-connected' || emailLoginInfo.status === 'expired') return (
                                <email.LoginButton
                                    org={org}
                                    accesstoken={accesstoken}
                                    onDone={onEmailRefreshConnection}
                                    sx={{ minWidth: 10, fontSize: '11px', textTransform: 'none' }}
                                />
                            )
                            return (
                                <ListItemText
                                    primary={
                                        <Box component="span" sx={{ fontSize: '11px', color: theme.palette.text.secondary }}>
                                            Sync with: {emailLoginInfo.displayName} (
                                            <Box
                                                component="span"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onEmailMenuOpen(e);
                                                }}
                                                sx={{
                                                    color: '#1976d2',
                                                    textDecoration: 'underline',
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        color: '#1565c0'
                                                    }
                                                }}
                                            >
                                                {emailLoginInfo.email}
                                            </Box>
                                            )
                                        </Box>
                                    }
                                />
                            )
                        })()}
                        <SidebarOutgoing
                            org={org}
                            accesstoken={accesstoken}
                        />
                    </ListItem>
                </List>
            )}
            {emailLoginInfo?.status === 'connected' && (
                <Menu
                    anchorEl={emailMenuAnchorEl}
                    open={emailMenuOpen}
                    onClose={onEmailMenuClose}
                >
                    <MenuItem onClick={() => {
                        onEmailMenuClose();
                        onEmailLogout();
                    }}>Disconnect Mailbox</MenuItem>
                </Menu>
            )}
            {!minimized && (
                <div style={{ padding: '0px 12px' }}>
                    <hr style={{ opacity: '0.3' }} />
                </div>
            )}
            {!minimized && (
                <SidebarNotifications
                    org={org}
                    opportunities={opportunities}
                    tradelines={tradelines}
                    notifications={notifications}
                    onUpdateNotification={onUpdateNotification}
                    accounts={accounts}
                    suppliers={suppliers}
                    accesstoken={accesstoken}
                    emailLoginInfo={emailLoginInfo}
                    emailConversations={emailConversations}
                />
            )}
            {!minimized && (
                <div style={{ padding: '0px 12px' }}>
                    <hr style={{ opacity: '0.3' }} />
                </div>
            )}
            {minimized && (
                <ListItem sx={{ color: theme.palette.text.secondary }}>
                    <Divider sx={{ width: '20px' }} />
                </ListItem>
            )}
            <List sx={{ padding: '4px 0px' }}>
                {_.compact([
                    minimized ? null : me.isAdmin ? {title: 'Pricing', icon: null } : null,
                    {title: 'Opportunities', icon: <FontAwesomeIcon icon={faMoneyCheckDollarPen} /> , path: '/opportunities' },
                    org?.settings?.bookings?.enabled ? {title: 'Bookings', icon: <FontAwesomeIcon icon={faShip} /> , path: '/bookings' } : null,
                    {title: 'Mailbox', icon: <FontAwesomeIcon icon={faEnvelopeDot} /> , path: '/email' },
                    me.isAdmin ? {title: 'Supplier Rates', icon: <FontAwesomeIcon icon={faCircleDollar} />, path: '/rates' } : null,
                    (me.showFinancials || me.email === 'admin@monada.ai') ? {title: 'Quotations Analytics', icon: <FontAwesomeIcon icon={faChartMixed} />, path: '/quotation-analytics' } : null,
                    {title: 'Tradelines', icon: <FontAwesomeIcon icon={faTable} />, path: '/opportunities-table' },
                    me.isAdmin ? {title: 'Sales', icon: null } : null,
                    {title: 'Accounts', icon: <FontAwesomeIcon icon={faBuilding} /> , path: '/accounts' },
                    {title: 'Sales Targets', icon: <FontAwesomeIcon icon={faBullseyeArrow} /> , path: '/tradelines' },
                    {title: 'Company Feed', icon: <FontAwesomeIcon icon={faList} />, path: '/company-feed' },
                    featureToggle['feature.campaigns-and-audiences'] && me.isAdmin ? {title: 'Audiences', icon: <FontAwesomeIcon icon={faPeopleGroup} /> } : null,
                    featureToggle['feature.campaigns-and-audiences'] && me.isAdmin ? {title: 'Campaigns', icon: <FontAwesomeIcon icon={faReplyClock} />, path: '/campaigns' } : null,
                    (me.showFinancials || me.email === 'admin@monada.ai') ? {title: 'Sales Targets Performance', icon: <FontAwesomeIcon icon={faChartMixed} />, path: '/sales-performance' } : null,
                    {title: 'Sales Management', icon: <FontAwesomeIcon icon={faChartTreeMap} />, path: '/sales-management' },
                    me.isAdmin ? {title: 'Templates & Forms', icon: null } : null,
                    me.isAdmin ? {title: 'Opportunity Templates', icon: <FontAwesomeIcon icon={faWpforms} />, path: '/templates' } : null,
                    me.isAdmin ? {title: 'Supplier & Rate Forms', icon: <FontAwesomeIcon icon={faFileDashedLine} />, path: '/forms' } : null,
                    me.isAdmin ? {title: 'Settings', icon: null } : null,
                    me.isAdmin || manages.length > 0 ? {title: 'Users', icon: <FontAwesomeIcon icon={faUsers} /> , path: '/users' } : null,
                    me.isAdmin ? {title: 'Suppliers', icon: <FontAwesomeIcon icon={faClipboard} />, path: '/suppliers' }: null,
                    me.isAdmin ? { title: 'Terms & Conditions', icon: <FontAwesomeIcon icon={faFileCheck} />, path: '/terms' } : null,
                    me.isAdmin ? { title: 'Site Headers', icon: <FontAwesomeIcon icon={faHeading} />, path: '/headers' } : null,
                    me.isAdmin ? { title: 'Generic Settings', icon: <FontAwesomeIcon icon={faCogs} />, path: '/settings' } : null,
                    false && me.isAdmin ? {title: 'Stats', icon: null } : null,
                    false && me.isAdmin ? { title: 'Procurement', icon: <FontAwesomeIcon icon={faChartArea} />, path: '/stats' } : null,
                ]).map((entry, index) => (
                    <ListItem  
                        button 
                        key={entry.path || entry.title} 
                        sx={{ 
                            color: theme.palette.text.secondary, 
                            background: location.pathname.includes(`/organization/${org.alias}${entry.path}`) ? alpha(theme.palette.text.secondary, 0.1) : '',
                            padding: '4px 8px',
                            margin: '2px 4px',
                            borderRadius: '4px',
                            border: '1px solid transparent',
                            fontSize: '11px',
                            minHeight: '32px',
                            '&:hover': {
                                border: '1px solid',
                                borderColor: alpha(theme.palette.text.secondary, 0.2),
                                backgroundColor: alpha(theme.palette.text.secondary, 0.05),
                            },
                            '&.Mui-disabled': {
                                opacity: 0.5,
                            },
                        }} 
                        disabled={!entry.path} 
                        onClick={() => navigate(`/organization/${org.alias}${entry.path}`)}
                        title={minimized ? entry.title : undefined}
                    >
                        {entry.icon && (
                            <Box sx={{ width: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: minimized ? '0px' : '8px' }}>
                                <ListItemIcon sx={{ color: theme.palette.text.secondary, minWidth: '32px', display: 'flex', justifyContent: 'center', '& svg': { fontSize: '14px' } }}>{entry.icon}</ListItemIcon>
                            </Box>
                        )}
                        {minimized && !entry.icon && (
                            <Divider sx={{ width: '20px' }} />
                        )}
                        {!minimized && (
                            <ListItemText primary={entry.title} primaryTypographyProps={{ sx: { fontSize: '11px', color: theme.palette.text.secondary, fontWeight: 400 } }} />
                        )}
                    </ListItem>
                ))}
            </List>
        </div>
    )
}

export default Sidebar;